<template>
  <div class="internal-info-container">
    <div class="sac__font-16 bold">Internal Information</div>
    <div class="sac__font-16 med n70">
      Service level and other engagement terms
    </div>
    <div class="flex-row" style="flex-wrap: wrap">
      <div class="input-container">
        <div class="sac__font-14 med mt-4">Partner Success Manager</div>
        <SACDropdown
          v-if="adminRole === 'admin' || adminRole === 'manager'"
          placeholder="Select Partner Success Manager"
          :options="getAdminPSM"
          :value="psmId"
          field="psmId"
          :change="handleChange"
        />
        <div v-else class="sac__font-14 med mt-4 n70">
          {{ findPSM(psmId) || '--' }}
        </div>
      </div>
      <div class="input-container">
        <div class="sac__font-14 med mt-4">HubSpot Company Id</div>
        <SACInput
          v-if="adminRole === 'admin' || adminRole === 'manager'"
          field="hsCompanyId"
          placeholder="Company Id"
          :value="hsCompanyId"
          :error="errorObj.hsCompanyIdErr"
          :change="handleChange"
        />
        <div v-else class="sac__font-14 med mt-4 n70">
          {{ hsCompanyId || '--' }}
        </div>
      </div>
      <div class="input-container">
        <div class="sac__font-14 med mt-4">Engagement Level</div>
        <SACDropdown
          v-if="adminRole === 'admin'"
          placeholder="Engagement Level"
          :options="engagementOptions"
          :value="engagementLevel"
          :error="errorObj.engagementLevelErr"
          field="engagementLevel"
          :change="handleChange"
        />
        <div v-else class="sac__font-14 med mt-4 n70">
          {{ engagementLevel || '--' }}
        </div>
      </div>
    </div>

    <div class="flex-row" style="flex-wrap: wrap">
      <div class="input-container">
        <div class="sac__font-14 med mt-4">Account Type</div>
        <SACDropdown
          v-if="adminRole === 'admin'"
          placeholder="Account Type"
          :options="accountStatusOptions"
          :value="corpStatus"
          :error="errorObj.corpStatusErr"
          field="corpStatus"
          :change="handleChange"
        />
        <div v-else class="sac__font-14 med mt-4 n70">
          {{ corpStatus || '--' }}
        </div>
      </div>
      <div class="input-container">
        <div class="sac__font-14 med mt-4">Account Status</div>
        <SACDropdown
          v-if="adminRole === 'admin'"
          placeholder="Account Status"
          :options="accountTypeOptions"
          :value="status"
          field="status"
          :error="errorObj.statusErr"
          :change="handleChange"
        />
        <div v-else class="sac__font-14 med mt-4 n70">
          {{ status || '--' }}
        </div>
      </div>
      <div class="input-container" v-if="!partnerCreationStep">
        <div class="sac__font-14 med mt-4">Promo Notification Limit (Per Quarter)</div>
        <SACDropdown
          v-if="adminRole === 'admin'"
          placeholder="Promo Limit"
          :options="promoQuarterlyLimitOptions"
          :value="promoQuarterlyLimit"
          :error="errorObj.promoQuarterlyLimitErr"
          field="promoQuarterlyLimit"
          :change="handleChange"
        />
        <div v-else class="sac__font-14 med mt-4 n70">
          {{ promoQuarterlyLimit || '--' }}
        </div>
      </div>
    </div>
    <div class="flex-row" style="flex-wrap: wrap">
      <div class="input-container d-flex just-center" v-if="!partnerCreationStep">
        <div class="flex-row align-center mt-6">
          <SACCheckbox
            field="notificationsAllowed"
            :checked="notificationsAllowed"
            :change="
              () => handleChange('notificationsAllowed', !notificationsAllowed)
            "
            label="Allow to send promo notifications?"
            :disable="adminRole !== 'admin'"
          />
        </div>
      </div>
      <div class="input-container d-flex just-center" v-if="!partnerCreationStep">
        <div class="flex-row align-center mt-6">
          <SACCheckbox
            field="monthlyHealthReport"
            :checked="monthlyHealthReport"
            :change="
              () => handleChange('monthlyHealthReport', !monthlyHealthReport)
            "
            label="Send Monthly Health Report Emails"
            :disable="adminRole !== 'admin'"
          />
        </div>
      </div>
      <div class="input-container d-flex just-center">
        <div class="flex-row align-center mt-6">
          <SACCheckbox
            field="apponlyAllowed"
            :checked="apponlyAllowed"
            :change="() => handleChange('apponlyAllowed', !apponlyAllowed)"
            label="Allow to create app-only plans?"
            :disable="adminRole !== 'admin'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import partnerSettingsMixin from '@shared/mixins/partnerSettingsMixin';
import SACDropdown from '@shared/components/sac/SACDropdown.vue';
import SACCheckbox from '@shared/components/sac/SACCheckbox.vue';
import SACInput from '@shared/components/sac/SACInput.vue';

export default {
  name: 'InternalInformation',
  components: { SACInput, SACDropdown, SACCheckbox },
  mixins: [partnerSettingsMixin],
  props: {
    handleChange: Function,
    hsCompanyId: String,
    psmId: Number,
    engagementLevel: String,
    promoQuarterlyLimit: String,
    partnerCreationStep: {
      type: Boolean,
      default: false,
    },
    corpStatus: String,
    status: String,
    serviceProvider: Boolean,
    apponlyAllowed: Boolean,
    notificationsAllowed: Boolean,
    monthlyHealthReport: Boolean,
    industry: String,
    errorObj: Object,
  },
  data() {
    return {
      accountStatusOptions: [
        { label: 'New', value: 'New' },
        { label: 'Pilot', value: 'Pilot' },
        { label: 'Partner', value: 'Partner' },
        { label: 'Test', value: 'Test' },
        { label: 'Other', value: 'Other' },
      ],
      promoQuarterlyLimitOptions: [
        { label: '1 (Default)', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 },
        { label: '5', value: 5 },
        { label: '6', value: 6 },
        { label: '7', value: 7 },
        { label: '8', value: 8 },
      ],
      engagementOptions: [
        { label: 'Engaged', value: 'Engaged' },
        { label: 'Unengaged', value: 'Unengaged' },
      ],
      accountTypeOptions: [
        { label: 'Active', value: 'Active' },
        { label: 'Inactive', value: 'Inactive' },
        { label: 'Cancelled', value: 'Cancelled' },
        { label: 'Deleted', value: 'Deleted' },
      ],
      partnerIndustryOptions: [
        {
          label: 'HVAC Service',
          value: 'HVAC Service',
        },
        {
          label: 'Energy Provider',
          value: 'Energy Provider',
        },
        {
          label: 'Insurance',
          value: 'Insurance',
        },
        {
          label: 'Retail',
          value: 'Retail',
        },
        {
          label: 'Cable/Internet',
          value: 'Cable/Internet',
        },
        {
          label: 'Healthcare',
          value: 'Healthcare',
        },
        {
          label: 'Other',
          value: 'Other',
        },
      ],
    };
  },
  computed: {
    getAdminPSM() {
      const { psmList } = this.$store.state.setting;
      const adminPSMOptions = psmList.map((p) => ({
        value: p.id,
        label: `${p.firstName} ${p.lastName}`,
      }));
      adminPSMOptions.unshift({
        value: -1,
        label: 'All PSMs',
      });
      return adminPSMOptions;
    },
  },
  mounted() {
    this.$store.dispatch('getAdminPSMList');
  },
  methods: {
    findPSM(psmId) {
      const { psmList } = this.$store.state.setting;
      const psm = psmList.find((p) => p.id === psmId);
      if (psm) {
        return `${psm.firstName} ${psm.lastName}`;
      }
      return null;
    },
  },
};
</script>

<style lang="scss">
.internal-info-container {
  display: flex;
  flex-direction: column;
  border: 1px dashed $PRIMARY;
  padding: 15px;

  // height: 510px;
  .delete-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
  }
}
.input-container {
  flex-direction: column;
  width: 440px;
  margin-right: 15px;
}
</style>
